/**
 * Admin Rules Profile tab ( item ).
 *
 * Shows the canonical intake + determination YAML (read-only; git-tracked)
 * alongside the active DB-cached version (version, loaded_at, yaml hash)
 * and a rendered prompt preview. A "Reload from canonical" button re-runs
 * the startup loader (operator only; audited). Editing the canonical YAML
 * is a git commit, not a UI action.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, FileText, CheckCircle2, AlertTriangle, Eye,
} from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import {
  getRulesProfile, reloadRulesProfile,
  type ProfileView,
} from '../../api/rulesProfile';

const KIND_LABELS: Record<string, string> = {
  intake: 'Intake Parsing Rules',
  determination: 'Determination Rules',
};

function shortHash(h: string | null | undefined): string {
  if (!h) return '-';
  return h.length > 12 ? h.slice(0, 12) : h;
}

function ProfilePanel({ view }: { view: ProfileView }) {
  const active = view.active;
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
          <FileText size={16} className="text-primary-600" />
          {KIND_LABELS[view.kind] ?? view.kind}
        </h3>
        {active ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            <CheckCircle2 size={12} /> active v{active.version}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
            <AlertTriangle size={12} /> no cached profile (hardcoded fallback)
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm mb-4">
        <div>
          <span className="text-slate-500">Active version:</span>{' '}
          <span className="font-medium">{active ? `v${active.version}` : '-'}</span>
        </div>
        <div>
          <span className="text-slate-500">Loaded at:</span>{' '}
          <span className="font-medium">
            {active?.loaded_at ? new Date(active.loaded_at).toLocaleString() : '-'}
          </span>
        </div>
        <div>
          <span className="text-slate-500">Canonical YAML hash:</span>{' '}
          <span className="font-mono text-xs">{shortHash(view.canonical_yaml_hash)}</span>
        </div>
        <div>
          <span className="text-slate-500">Canonical file:</span>{' '}
          <span className="font-medium">
            {view.canonical_present ? 'present' : 'missing'}
          </span>
        </div>
      </div>

      {/* Canonical YAML (read-only) */}
      <div className="mb-4">
        <div className="text-xs font-medium text-slate-500 mb-1">
          Canonical YAML (read-only; edit via git commit)
        </div>
        <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs font-mono overflow-auto max-h-72 whitespace-pre-wrap break-words">
          {view.canonical_yaml ?? '// canonical file missing'}
        </pre>
      </div>

      {/* Rendered prompt previews */}
      <div>
        <div className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
          <Eye size={12} /> Prompt previews (base template + active rules injection)
        </div>
        {view.prompt_previews.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No prompt sections defined.</p>
        ) : (
          <div className="space-y-3">
            {view.prompt_previews.map((p) => (
              <details key={p.section} className="border border-slate-200 rounded">
                <summary className="cursor-pointer text-xs font-medium text-slate-600 px-3 py-1.5 select-none">
                  section: <span className="font-mono">{p.section}</span>
                </summary>
                <div className="px-3 pb-3 pt-1">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
                    base template
                  </div>
                  <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] font-mono overflow-auto max-h-40 whitespace-pre-wrap break-words mb-2">
                    {p.base_prompt ?? '// section missing -> hardcoded fallback in use'}
                  </pre>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
                    rendered with active rules
                  </div>
                  <pre className="bg-slate-50 border border-slate-200 rounded p-2 text-[11px] font-mono overflow-auto max-h-40 whitespace-pre-wrap break-words">
                    {p.rendered_with_rules ?? '// no active profile (hardcoded fallback)'}
                  </pre>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RulesProfileTab() {
  const { addToast } = useToastStore();
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [intake, setIntake] = useState<ProfileView | null>(null);
  const [determination, setDetermination] = useState<ProfileView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getRulesProfile();
      setIntake(data.intake);
      setDetermination(data.determination);
    } catch (err) {
      addToast('Failed to load rules profile.', 'error');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { void load(); }, [load]);

  const handleReload = async () => {
    setReloading(true);
    try {
      const summary = await reloadRulesProfile();
      addToast('Rules profile reloaded from canonical YAML.', 'success');
      // eslint-disable-next-line no-console
      console.info('reload summary', summary);
      await load();
    } catch (err) {
      addToast('Rules-profile reload failed.', 'error');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setReloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="animate-spin" size={20} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 max-w-2xl">
          Configurable rules profile for intake parsing and determination. The
          canonical YAML is git-tracked (the audited baseline); the DB cache is
          loaded at startup. Editing the canonical file is a git commit, not a
          UI action. Reload re-reads the canonical YAML into the cache (audited).
        </p>
        <button
          onClick={handleReload}
          disabled={reloading}
          className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
        >
          {reloading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Reload from canonical
        </button>
      </div>

      {intake && <ProfilePanel view={intake} />}
      {determination && <ProfilePanel view={determination} />}
    </div>
  );
}